import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup,ReactiveFormsModule,ValidationErrors,ValidatorFn,Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Client } from '../../models/client';
import { RegisterFormService } from '../../services/register-form.service';
import { TurnstileService } from '../../services/turnstile.service';
import { DialogErrorComponent } from '../dialog-error/dialog-error.component';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { Subscription } from 'rxjs';

declare var fbq: any;

/**
 * Name pattern shared with the backend registration validation: an optional
 * leading apostrophe, then a letter, then letters, marks, spaces, dots,
 * apostrophes, hyphens and the zero-width joiners used by some scripts.
 * Declared as a `RegExp` with the `u` flag so `\p{L}` and `\p{M}` work.
 */
const NAME_PATTERN = /^['’]?[\p{L}\p{M}][\p{L}\p{M} .'’\u{200C}\u{200D}-]*$/u;

const NAME_MAX_LENGTH = 50;

/**
 * Public lead registration form of the landing.
 *
 * Renders either the four-field form (contact page and modal) or the two-field
 * form (home and CRM page) and submits both through the same registration
 * request. Every instance owns a managed Turnstile widget shown above the
 * submit button: the button stays disabled until the widget has verified the
 * visitor, and the request is never sent without a captcha token.
 */
@Component({
	selector: 'app-form',
	templateUrl: './form.component.html',
	styleUrls: ['./form.component.css'],
	standalone: true,
	imports: [CommonModule, TranslateModule, ReactiveFormsModule, Ng2TelInputModule],
})
export class FormComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('messageModal') messageModal: TemplateRef<any>;
	@ViewChild('captchaContainer') captchaContainer?: ElementRef<HTMLDivElement>;
	@Input() fieldsNumber: number = 4;
	@Input() section: string = 'contacts';
	@Input() openModal: boolean = false;
	private dialogErrorModalRef: BsModalRef;
	private initialValuesRegisterForm: FormGroup;
	private captchaWidgetId?: string;
	private captchaRender?: Promise<string>;

	//public openModal: boolean;
	public registerForm: FormGroup;
	public registerFormDuo: FormGroup;
	public invalidField = false;
	public isSubmitting = false;
	public captchaReady = false;
	public backMessage = '';
	/* phone number variables */
	public phoneCountryAbbreviation = '';
	public phoneCode = 0;
	public flagFirstName: boolean;
	public flagLastName: boolean;
	public flagEmail: boolean;
	public flagPhone: boolean;
	public isMobile: boolean = false;
	public isTablet: boolean = false;
	private formSubs: Subscription[] = [];

	constructor(
		private formBuilder: FormBuilder,
		private modalService: BsModalService,
		private registerFormService: RegisterFormService,
		public registerFormModalRef: BsModalRef,
		public messageModalRef: BsModalRef,
		private screenSizeService: ScreenSizeService,
		private turnstileService: TurnstileService,
		private translateService: TranslateService,
		private changeDetectorRef: ChangeDetectorRef,
	) {
		this.isMobile = this.screenSizeService.isResponsiveSM;
		this.isTablet = this.screenSizeService.isResponsiveMD;

		const resizeSub = this.screenSizeService.isResposiveView$.subscribe((resResize) => {
			this.isMobile = resResize.mobile;
			this.isTablet = resResize.tablet;
		});
	  
		this.formSubs.push(resizeSub);

		this.openModal = false;
		this.flagFirstName = false;
		this.flagLastName= false;
		this.flagEmail=false;
		this.flagPhone=false;
		this.initialValuesRegisterForm = this.registerForm?.value;
		this.registerForm = this.formBuilder.group({
			firstName: ['', [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_MAX_LENGTH)]],
			lastName: ['', [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_MAX_LENGTH)]],
			email: ['', [Validators.required,Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
			phone: ['', [Validators.required, Validators.pattern(/^([0-9])*$/), Validators.maxLength(16)]],
		});
		
		this.registerFormDuo = this.formBuilder.group({
			fullname: ['', [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_MAX_LENGTH)]],
			celphone: ['', [Validators.required, Validators.pattern(/^([0-9])*$/), Validators.maxLength(16)]],
		});

		
	}

	public get form(): { [key: string]: AbstractControl } {
		return this.registerForm.controls;
	}
	public get formDuo(): { [key: string]: AbstractControl } {
		return this.registerFormDuo.controls;
	}

	/**
	 * Relaxes the last name validation for the modal variant.
	 */
	ngOnInit(): void {
		if (this.openModal) {
			this.registerForm.get('lastName')?.setValidators(null);
			this.registerForm.get('lastName')?.updateValueAndValidity();
		}
	}

	/**
	 * Renders the captcha widget once the form that hosts its container exists,
	 * and redraws it whenever the visitor switches the site language so the
	 * widget follows the new language without a page reload.
	 */
	ngAfterViewInit(): void {
		this.ensureCaptchaWidget().catch((error: unknown) => console.warn('Captcha widget not ready', error));
		const langSub = this.translateService.onLangChange.subscribe(() => this.redrawCaptcha());
		this.formSubs.push(langSub);
	}

	/**
	 * Renders the managed captcha widget for this form instance once, in the
	 * current site language, and returns its widget id. The widget reports its
	 * token state back so the submit button is enabled only while a token is
	 * available. Concurrent calls share the same render; a failed render is
	 * forgotten so the next call can try again.
	 */
	private ensureCaptchaWidget(): Promise<string> {
		if (!this.captchaRender) {
			const container = this.captchaContainer?.nativeElement;
			if (!container) {
				return Promise.reject(new Error('Captcha container not rendered'));
			}
			this.captchaRender = this.turnstileService
				.render(container, {
					language: this.translateService.currentLang,
					onTokenChange: (token) => this.onCaptchaTokenChange(token),
				})
				.then((widgetId) => {
					this.captchaWidgetId = widgetId;
					return widgetId;
				})
				.catch((error: unknown) => {
					this.captchaRender = undefined;
					throw error;
				});
		}
		return this.captchaRender;
	}

	/**
	 * Removes the current captcha widget and renders a new one in the current
	 * site language. The previous token is discarded, so the submit button is
	 * disabled again until the new widget verifies the visitor.
	 */
	private redrawCaptcha(): void {
		this.removeCaptcha();
		this.ensureCaptchaWidget().catch((error: unknown) => console.warn('Captcha widget not ready', error));
	}

	/**
	 * Removes this instance's captcha widget from the page and clears its state.
	 */
	private removeCaptcha(): void {
		if (this.captchaWidgetId) {
			this.turnstileService.remove(this.captchaWidgetId);
		}
		this.captchaWidgetId = undefined;
		this.captchaRender = undefined;
		this.captchaReady = false;
	}

	/**
	 * Tracks whether the widget currently holds a token and refreshes the view,
	 * since Turnstile invokes its callbacks outside Angular's change detection.
	 */
	private onCaptchaTokenChange(token: string | null): void {
		this.captchaReady = token !== null;
		this.changeDetectorRef.detectChanges();
	}

	/**
	 * Obtains the captcha token for one submission, rendering the widget first
	 * if the initial render failed or has not finished yet.
	 */
	private async requestCaptchaToken(): Promise<string> {
		const widgetId = await this.ensureCaptchaWidget();
		return this.turnstileService.getToken(widgetId);
	}

	/**
	 * Resets the captcha widget after a submission attempt so it verifies again
	 * and produces a new token; tokens are single-use regardless of the outcome.
	 */
	private resetCaptcha(): void {
		if (this.captchaWidgetId) {
			this.turnstileService.reset(this.captchaWidgetId);
		}
	}

	/**
	 * Shows the error dialog with the translated captcha failure message,
	 * used both when the widget cannot produce a token and when the backend
	 * rejects the token.
	 */
	private showCaptchaError(): void {
		this.showErrorDialog(this.translateService.instant('SHARED.form.validators.captchaFailed'));
	}

	/**
	 * Opens the shared error dialog with the given message.
	 */
	private showErrorDialog(message: string): void {
		this.dialogErrorModalRef = this.modalService.show(DialogErrorComponent, { initialState: { message } });
	}

	telInputObject(obj: any) {
		obj.setCountry('ua');
		this.phoneCountryAbbreviation = obj.selectedCountryData?.iso2;
		this.phoneCode = obj.selectedCountryData?.dialCode;
		}

	onCountryChange(event: any) {
		this.phoneCountryAbbreviation = event.iso2;
		this.phoneCode = event.dialCode;
	}

	// Metodo para validar la entrada  sólo números y teclas de control 
	validateNumber(event: KeyboardEvent): void {
		const key = event.key;
		if (!/^\d$/.test(key) && key !== 'Backspace' && key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Delete') {
		  event.preventDefault();
		}
	  }

	onKey(nameLabel: string) {
		if (nameLabel === 'firstName') {
			this.flagFirstName = true
		}
		if (nameLabel === 'lastName') {
			this.flagLastName = true
		}
		if (nameLabel === 'email') {
			this.flagEmail = true
		}
		if (nameLabel === 'phone') {
			this.flagPhone = true;
		}
		
	}
	/**
	 * Submits a lead: reads the captcha token, sends the registration request
	 * with it and reacts to the outcome.
	 *
	 * Without a token nothing is sent and the captcha error is shown. A `403`
	 * means the backend refused the token, so the same captcha error is shown;
	 * any other client error surfaces the backend message, while server and
	 * network errors show the generic message so no internal detail reaches
	 * the visitor. The widget is reset after every response because tokens
	 * are single-use.
	 */
	public async postClient(client: Client): Promise<void> {
		this.isSubmitting = true;
		let captchaToken: string;
		try {
			captchaToken = await this.requestCaptchaToken();
		} catch (error) {
			console.error('Captcha token unavailable', error);
			this.isSubmitting = false;
			this.resetCaptcha();
			this.showCaptchaError();
			return;
		}

		this.registerFormService.registerClient(client, captchaToken).subscribe(
			(res) => {
				this.resetCaptcha();
				this.backMessage = res.message;
				this.messageModalRef = this.modalService.show(this.messageModal);
				this.registerFormModalRef?.hide();
				this.isSubmitting = false;
				this.invalidField = false;
				this.registerForm.reset(this.initialValuesRegisterForm);
				this.registerFormDuo.reset(this.initialValuesRegisterForm);
				this.flagFirstName = false;
				this.flagLastName = false;
				this.flagEmail = false;
				this.flagPhone = false;
				try {
					this.trackCompleteRegistration(client);
				  } catch (error) {
					console.error("Error tracking data", error);          
				  }
		  
			},
			(err: HttpErrorResponse) => {
				this.isSubmitting = false;
				this.resetCaptcha();
				if (err.status === 403) {
					this.showCaptchaError();
					return;
				}
				this.showErrorDialog(this.resolveErrorMessage(err));
			}
		);
	}

	/**
	 * Picks the message to show for a failed registration: the backend message
	 * for client errors (`4xx`) that carry one, the generic translated message
	 * for server errors, network failures and responses without a message.
	 */
	private resolveErrorMessage(err: HttpErrorResponse): string {
		const isClientError = err.status >= 400 && err.status < 500;
		const backendMessage: unknown = err.error?.message;
		if (isClientError && typeof backendMessage === 'string' && backendMessage) {
			return backendMessage;
		}
		return this.translateService.instant('ERROR.subtitle');
	}
	public listenText(event: Event, controlName: string, formName: string) {
		const inputElement = event.target as HTMLInputElement;
		const originalValue = inputElement.value;
		const trimmedValue = inputElement.value.trim();
		const cleanedValue = trimmedValue.replace(/\s{2,}/g, ' ');
		if (originalValue !== cleanedValue) {
			// Actualizamos el valor en el campo
			inputElement.value = cleanedValue;

			// Actualizamos el valor del control en el formulario correspondiente
			const form = formName === 'registerForm' ? this.registerForm : this.registerFormDuo;
			const control = form.get(controlName);
			if (control) {
				control.setValue(cleanedValue);
				control.updateValueAndValidity();
			}
		}
	}

	onEmailInput(event: Event) {
		const inputElement = event.target as HTMLInputElement;
		const cleanedValue = inputElement.value.replace(/\s/g, ''); // Eliminar espacios en blanco
		inputElement.value = cleanedValue; // Actualizar el valor del campo
		const control = this.registerForm.get('email');
		// También puedes actualizar el valor del control en el formulario
		
		if (control !== null) { 
			control.setValue(cleanedValue)
		}
	}

	

	public registerClientDuo(): void {
		if (this.registerFormDuo.invalid) {
			this.invalidField = true;
			return;
		}
		const auxClient: Client = {
			firstName: this.registerFormDuo.get('fullname')?.value, 
			phoneCountryAbbreviation: this.phoneCountryAbbreviation?.toUpperCase(),
			phoneCode: '+' + this.phoneCode?.toString(),
			phone: this.registerFormDuo.get('celphone')?.value,
		}
		void this.postClient(auxClient);
	}


	public registerClient(): void {
		const SpacesRegularExpression = /(\s{2,})/g
		if (this.section === 'crm') {
			this.registerForm.get('lastName')?.setValidators(null);
			this.registerForm.get('email')?.setValidators(null);
			this.registerForm.get('lastName')?.updateValueAndValidity();
			this.registerForm.get('email')?.updateValueAndValidity();
		}

		if (this.registerForm.invalid) {
			this.invalidField = true;
			return;
		}

		this.isSubmitting = true;

		const client: Client = {
			firstName: this.registerForm.get('firstName')?.value.trim()
				.replace(SpacesRegularExpression, ' '), 
			lastName: this.registerForm.get('lastName')?.value.trim()
				.replace(SpacesRegularExpression, ' '),
			email: this.registerForm.get('email')?.value,
			phoneCountryAbbreviation: this.phoneCountryAbbreviation?.toUpperCase(),
			phoneCode: '+' + this.phoneCode?.toString(),
			phone: this.registerForm.get('phone')?.value,
		};

		if (this.openModal) {
			delete client.lastName;
		}
		void this.postClient(client);
	}

	public closeMessageModal(): void {
		this.messageModalRef?.hide();
		this.registerForm.reset();
	}

	/**
	 * Reports a completed registration to Facebook Pixel and Google Tag Manager.
	 * Each tracker is called only when its global is present, since both are
	 * injected by third-party scripts that may be blocked or absent locally.
	 */
	public trackCompleteRegistration(data: Client): void {
		if (typeof fbq === 'function') {
			fbq('track', 'CompleteRegistration', data);
		}

		if (typeof window.gtag === 'function') {
			window.gtag('event', 'conversion_event_submit_lead_form', {
				event_category: 'Lead',
				event_label: 'Submit Form',
				value: {
					firstName: data.firstName,
					lastName: data.lastName,
					email: data.email,
					phone: data.phone,
					phoneCode: data.phoneCode,
				},
			});
		}
	}
	

	/**
	 * Hides the modal and removes this instance's captcha widget from the page.
	 */
	ngOnDestroy(): void {
		this.registerFormModalRef?.hide();
		this.removeCaptcha();
		this.formSubs.forEach((sub) => sub.unsubscribe());
	}
}
